import { Box, Button, Card, IconButton, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { FC, useRef, useState } from "react";
import { ListContextProvider, useDeleteWithConfirmController, useListController } from "ra-core";
import { Content } from "~/components/content/Content";
import { OdResourcePageProps } from "~/types/resource";
import Scrollbar from "~/components/Scrollbar";
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import { Datagrid } from "~/components/list/datagrid";
import { FunctionField } from "~/components/field";
import { RegisterCodeResponse } from "~/gen/api/user";
import { RecPatch } from "~/dataProvider/tools";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import CreateRegisterCode from "./CreateRegisterCode";
import ReadonlyField from "~/components/input/ReadonlyField";
import { autoCopyTextFieldProps } from "~/utils/clipboard";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import Confirm from "~/components/widgets/Confirm";


const resource = 'settings-clients-securefile-register-code'

const SecurefileRegisterCode: FC<OdResourcePageProps&{ close: ()=>void}> = ({
  options,
  close,
}) => {
  const listCtrl = useListController({ resource, basePath: '', filter: {} })
  const { refetch } = listCtrl

  const format = useUserTimeFormat()
  const [states, setStates] = useState({
    openCreate: false,
    recordToBeVoided: null as RegisterCodeResponse|null,
    copied: false,
  })
  
  const delHandlers = useDefaultSavedHandlers('Invalidate Registration Code');
  delHandlers.onSuccess.merge(refetch)
  
  const tempDelRec = useRef(null as RegisterCodeResponse|null)
  const delRec = states.recordToBeVoided ?? tempDelRec.current

  const {
    open: openConfirm,
    loading,
    handleDelete,
    handleDialogClose,
    handleDialogOpen,
  } = useDeleteWithConfirmController({ 
    resource,
    record: states.recordToBeVoided as any,
    ...delHandlers,
  })

  const theme = useTheme()
  const now = new Date()
  const rowStyles = (d:RegisterCodeResponse&RecPatch) => {
    const expired = d.voided || new Date(d.expiration!) < now
    return expired ? { backgroundColor: alpha(theme.palette.error.light, 0.05) } : {}
  }
  return (
    <Content 
      pageName="Registration Code" 
      resourceName="Registration Code" 
      breadcrumbs={options?.breadcrumbs}
      mainActions={
        <>
          <Button 
            color="primary"
            variant="outlined"
            onClick={() => {
              close()
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={
              <AddIcon />
            }
            onClick={() => {
              setStates(v => ({...v, openCreate: true}))
            }}
          >
            Add Registration Code
          </Button>
        </>
      }
    >
      <ListContextProvider value={listCtrl}>
        <Card>
          <Scrollbar>
            <Box sx={{ minWidth: 1150 }}>
              <Datagrid 
                hasBulkActions={false} 
                sx={{ 
                  '& th' : { py: 2 },
                  '& td' : { py: 2 },
                }}
                rowStyle={rowStyles}
              >
                <FunctionField source="code" label="Registration Code" sortable={false} 
                  render={(d?:RegisterCodeResponse&RecPatch) => (
                    <Box
                      sx={{
                        width: 350, height: 40,
                        'p.masked': { visibility: 'visible', display: 'block', lineHeight: '40px', ml: 2, },
                        'div.box': { visibility: 'hidden', display: 'none' },
                        '&:hover': {
                          'p.masked': { visibility: 'hidden', display: 'none' },
                          'div.box': { visibility: 'visible', display: 'block' },
                        }
                      }}
                    >
                      <Typography variant="h6" component="p" className="masked"><code>************</code></Typography>
                      <Tooltip title={states.copied ? 'Copied' : 'Click to Copy'}>
                        <Box className="box">
                          <ReadonlyField 
                            id={`code-generated${d?.codeId}`}
                            size="small"
                            value={d!.code!}
                            {...autoCopyTextFieldProps(
                              () => setStates(v => ({...v, copied: true})), 
                              () => setTimeout(() => setStates(v => ({...v, copied: false})), 500)
                            )}
                            {...{inputProps: {tabIndex: 0}}}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                  )}
                />
                <FunctionField source="creatorId" label="Creator" sortable={false} 
                  render={(d?:RegisterCodeResponse&RecPatch) => (
                    <Tooltip title={d?.creatorUserame ?? ''}>
                      <Box>
                        <Typography variant="body2">
                          {d?.creatorFirstName} {d?.creatorLastName}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                />
                <FunctionField source="createTime" label="Validity Period" sortable={false} 
                  render={(d?:RegisterCodeResponse&RecPatch) => (
                    <Box
                      sx={{
                        my: -0.5,
                        'p.MuiTypography-caption > span.h': { display: 'inline-block', width: 36},
                        'p.MuiTypography-caption > span.until': { color: theme => theme.palette.primary.main },
                        'p.MuiTypography-caption > span.until.voided': { color: theme => theme.palette.error.light },
                      }}
                    >
                      <Typography variant="caption" component="p">
                        <span className="h">From</span><span>{format(d?.createTime!, 'Pp')}</span>
                      </Typography>
                      <Typography variant="caption" component="p">
                        <span className="h">Until</span> 
                        <span className={`until ${d?.voided || new Date(d!.expiration!) < now ? 'voided' : ''}`}>
                          {format(d?.expiration!, 'Pp')}
                        </span>
                      </Typography>
                    </Box>
                  )}
                />
                <FunctionField source="maxAllowedUsage" sortable={false} 
                  label={
                    <Box sx={{ my: -0.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: -0.25 }}>Code Usage</Typography>
                      <Typography variant="caption" color="InactiveCaptionText">Actual / Max Allowed</Typography>
                    </Box>
                  } 
                  render={(d?:RegisterCodeResponse&RecPatch) => (
                    <Typography variant="body2" color={d?.voided ? 'error' : undefined}>
                      <Typography variant="body2" component="span">{d?.actualUsage}</Typography>{" / "}
                      <Typography variant="body2" component="span">{d?.maxAllowedUsage != null ? d.maxAllowedUsage : 'Unlimited'}</Typography>
                      {d?.voided && 
                        <Typography variant="caption" component="p">Voided</Typography>}
                    </Typography>
                  )}
                />
                <FunctionField label="Action" sortable={false} textAlign="center" 
                  render={(d?:RegisterCodeResponse&RecPatch) => (
                    d && !d.voided && new Date(d.expiration!) > now &&
                    <>
                      <Tooltip title="Delete">
                        <IconButton
                          sx={{
                            color: 'text.primary' as any,
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                          onClick={(e:any) => {
                            setStates(v => ({...v, recordToBeVoided: d}))
                            tempDelRec.current = null
                            handleDialogOpen(e)
                          }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                />
              </Datagrid>
            </Box>
          </Scrollbar>
          {//@ts-ignore 
          <DefaultPagination {...listCtrl} />}
        </Card>
        {states.openCreate && 
          <CreateRegisterCode open={states.openCreate} 
            setOpen={(x:boolean) => setStates(v => ({...v, openCreate: x})) } 
          />}
      </ListContextProvider>
      <Confirm
        isOpen={openConfirm}
        title={"Declare Registration Code Voided"}
        loading={loading}
        onConfirm={handleDelete}
        onClose={(e:any) => {
          handleDialogClose(e); 
          setStates(v => {
            const v1 = ({...v, recordToBeVoided:null})
            tempDelRec.current = v.recordToBeVoided
            return v1
          })
          setTimeout(() => { tempDelRec.current = null }, 5000)
        }}
        confirm="Confirm"
        cancel="Cancel"
        content={(
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1 }}
              >
                Please confirm whether to invalidate this
                "{delRec?.code}"
                Registration Code.
              </Typography>
            </Box>
          </Box>
        )}
      />
    </Content>
  )
}

export default SecurefileRegisterCode;
